"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3.5">{text}</span>;
}
function Divider() {
  return <div className="w-11 h-[3px] bg-blue-700 mb-6" />;
}
/* ─── Data ────────────────────────────────────────────────── */
const CHALLENGES = [
  { challenge: "Lost leads and follow-up failures",        solution: "Automated lead tracking with smart reminders and workflows" },
  { challenge: "No visibility into sales pipeline",         solution: "Real-time pipeline dashboard with stage-by-stage tracking" },
  { challenge: "Customer data scattered everywhere",        solution: "Single, centralised customer database with full interaction history" },
  { challenge: "Sales team wasting time on manual tasks",   solution: "Automate 70% of repetitive sales tasks and reporting" },
  { challenge: "Poor customer retention",                   solution: "360° customer view enables personalised engagement and proactive service" },
  { challenge: "No reporting or analytics",                 solution: "Real-time reports, conversion tracking and forecasting dashboards" },
];

const FEATURES = [
  {
    n: "01", title: "Pipeline Management",
    body: "Visualise your entire sales pipeline. Drag-and-drop stages, real-time deal value tracking, probability scoring and AI-powered deal recommendations.",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    tags: ["Drag-and-drop stages", "Probability scoring", "AI recommendations"],
  },
  {
    n: "02", title: "Sales Automation",
    body: "Automate follow-up emails, task assignments, meeting scheduling and status updates. Reduce manual work by 70% and eliminate sales slip-through.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80",
    tags: ["70% less manual work", "Auto follow-ups", "Meeting scheduling"],
  },
  {
    n: "03", title: "Contact & Account Management",
    body: "Complete 360° view of every contact — call logs, email history, meetings, deals and documents. Rich profiles with custom fields for your industry.",
    img: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600&q=80",
    tags: ["360° contact view", "Custom fields", "Full interaction history"],
  },
  {
    n: "04", title: "Email & WhatsApp Integration",
    body: "Send emails and WhatsApp messages directly from the CRM. Track opens, clicks and replies. Automate sequences for nurturing and onboarding.",
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80",
    tags: ["WhatsApp Business API", "Open & click tracking", "Automated sequences"],
  },
  {
    n: "05", title: "Reports & Dashboards",
    body: "Real-time analytics on revenue, conversion rates, team performance and customer health. Custom dashboards for sales managers and executives.",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=600&q=80",
    tags: ["Revenue analytics", "Team performance", "Custom dashboards"],
  },
  {
    n: "06", title: "Third-Party Integrations",
    body: "Connect with your existing tools: Gmail, Outlook, WhatsApp Business API, Razorpay, Tally, Zoho, Mailchimp and 50+ other business applications.",
    img: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&q=80",
    tags: ["50+ integrations", "Razorpay / Tally", "Gmail / Outlook"],
  },
];

const DEPLOYMENTS = [
  {
    title: "Cloud-Based SaaS",
    desc: "Access from any device, anywhere. Monthly subscription, zero infrastructure cost.",
    detail: "Best for: Most businesses — fastest setup, automatic updates, no maintenance.",
  },
  {
    title: "On-Premises Deployment",
    desc: "Full data control on your own servers. One-time licence + annual support.",
    detail: "Best for: Regulated industries, enterprises with strict data residency requirements.",
  },
  {
    title: "Hybrid Model",
    desc: "Cloud frontend with on-premises database — best of both security and accessibility.",
    detail: "Best for: Businesses needing cloud convenience with on-premises data control.",
  },
  {
    title: "Custom White-Label",
    desc: "Your brand, your domain, your experience — built on our CRM infrastructure.",
    detail: "Best for: Agencies, SaaS resellers and enterprises wanting a branded platform.",
  },
];

const STEPS = [
  { n: "01", title: "Business Process Assessment",  body: "We map your current sales process, customer touchpoints and workflow gaps to define the CRM configuration scope." },
  { n: "02", title: "Platform & Deployment Selection", body: "We recommend the right deployment model — cloud, on-premises, hybrid, or white-label — based on your needs and budget." },
  { n: "03", title: "CRM Configuration & Setup",    body: "We configure pipelines, stages, custom fields, user roles, automations and integrations to match your exact workflow." },
  { n: "04", title: "Data Migration",               body: "We migrate your existing contacts, deals and history from spreadsheets, legacy CRMs, or other systems — clean and complete." },
  { n: "05", title: "Team Training",                body: "Live training sessions for all user roles — sales team, managers and admins — with documentation and video walkthroughs." },
  { n: "06", title: "Go-Live & Ongoing Support",   body: "Managed go-live with hypercare support, post-launch optimisation and a dedicated CRM specialist for ongoing queries." },
];

const FAQS = [
  { q: "What makes Customer Maximiser different from Salesforce or HubSpot?",  a: "Customer Maximiser is built specifically for Indian SMEs and MSMEs — with local integrations (Razorpay, Tally, WhatsApp Business), rupee-based pricing and a team that understands the Indian sales process. Unlike generic platforms, we configure it to match exactly how your business works." },
  { q: "How long does CRM implementation take?",                               a: "Basic setups are live in 2–4 weeks. Full implementations with data migration, custom automations and integrations typically take 6–12 weeks. We provide a detailed timeline after the process assessment." },
  { q: "Can we migrate data from our existing CRM or spreadsheets?",           a: "Yes. We handle complete data migration from Excel, Google Sheets, Zoho, Salesforce, HubSpot and legacy CRM systems — clean, mapped and validated before go-live." },
  { q: "Will our sales team actually use it?",                                  a: "Adoption is our priority. We provide role-specific training, simplified mobile views for field sales and a change management plan. Most clients see 90%+ adoption within the first month." },
  { q: "Can Customer Maximiser integrate with WhatsApp and Tally?",            a: "Yes. Native WhatsApp Business API integration for messaging from within CRM and Tally integration for syncing invoices, payments and account data — both included in our implementation." },
  { q: "What does support look like after go-live?",                           a: "We offer tiered support plans — from standard ticket-based support to a dedicated CRM specialist on retainer. All plans include access to training resources, release notes and quarterly optimisation reviews." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Feature Card ────────────────────────────────────────── */
function FeatureCard({ s, i }: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[170px] overflow-hidden">
        <img
          src={s.img} alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-[14px] font-bold text-slate-900 mb-2.5 leading-snug">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed mb-4">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last?: boolean }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start relative transition-all duration-500 ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Deployment Card ─────────────────────────────────────── */
function DeployCard({ d, i }: { d: { title: string; desc: string; detail: string }; i: number }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-white border border-slate-200 p-6 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${i * 0.1}s` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-blue-700 flex items-center justify-center flex-shrink-0">
          <span className="text-[11px] font-black text-white">{String(i + 1).padStart(2, "0")}</span>
        </div>
        <p className="text-[14px] font-bold text-slate-900 leading-snug">{d.title}</p>
      </div>
      <p className="text-[13.5px] text-slate-500 leading-relaxed mb-3">{d.desc}</p>
      <p className="text-[12px] font-semibold text-blue-700 tracking-wide">{d.detail}</p>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function CRMPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>Customer Maximiser CRM</span>
          </nav>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-5">
            {["Sales Automation", "Pipeline Management", "WhatsApp CRM", "Analytics", "Custom Workflows"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — Customer Relationship Management" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 max-w-[580px]">
            Customer Maximiser — The CRM That Grows With You
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            More than a CRM — a complete customer success platform that automates sales, centralises customer data and drives sustainable revenue growth. Fully customisable, built for the way your business actually works.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              GET FREE DEMO
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              VIEW PRICING
            </button>
          </div>
        </div>

        {/* Right: photo */}
        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/CRM.png"
            alt="CRM dashboard"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "70%",    l: "Less Manual Work" },
          { v: "360°",   l: "Customer View" },
          { v: "50+",    l: "Integrations" },
          { v: "4",      l: "Deployment Options" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ WHY CUSTOMER MAXIMISER — challenge table ═══════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Why Customer Maximiser" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Solve Your Biggest Sales & Customer Challenges
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Every challenge in the table is a real problem we hear from Indian businesses every week. Customer Maximiser is built to solve them — not work around them.
            </p>

            {/* Challenge → Solution table */}
            <div className="border-t-2 border-blue-700">
              {CHALLENGES.map((row, i) => (
                <Reveal key={i} from="left" delay={(i % 4) * 0.07}>
                  <div
                    className="grid grid-cols-2 gap-4 py-4 border-b border-slate-100 items-start"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 bg-orange-500 flex-shrink-0 mt-1.5" />
                      <span className="text-[13px] font-semibold text-slate-700 leading-snug">{row.challenge}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 bg-blue-700 flex-shrink-0 mt-1.5" />
                      <span className="text-[13px] text-slate-500 leading-snug">{row.solution}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80"
                alt="CRM analytics dashboard"
                className="w-full h-[480px] object-cover"
              />
              {/* Floating stat */}
              <div className="absolute bottom-[-24px] right-[-24px] bg-white border border-slate-200 shadow-xl px-6 py-5 hidden lg:block">
                <p className="text-[30px] font-black text-blue-700 leading-none">70%</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Less Manual Work</p>
                <p className="text-[28px] font-black text-orange-600 leading-none mt-4">360°</p>
                <p className="text-[11px] text-slate-400 mt-1.5 tracking-[0.06em] uppercase font-medium">Customer View</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ KEY FEATURES — 3×2 card grid ═══════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="Key Features" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Six Powerful Capabilities.<br />One Unified Platform.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  Everything your sales and customer success team needs — in a single, connected CRM.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((s, i) => <FeatureCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ DEPLOYMENT OPTIONS ═════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="Deployment Options" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Deploy the Way Your Business Needs
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              Customer Maximiser is available in four deployment models — from instant cloud access to fully private on-premises infrastructure. Choose what works best for your security, compliance and budget requirements.
            </p>
            <div className="flex flex-col gap-0.5">
              {DEPLOYMENTS.map((d, i) => <DeployCard key={i} d={d} i={i} />)}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <img
              src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=700&q=80"
              alt="CRM deployment"
              className="w-full h-[560px] object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* ══ IMPLEMENTATION PROCESS ═════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Implementation Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              6-Step CRM Implementation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A structured rollout that puts your team first — starting with your actual workflows and ending with full adoption, not just installation.
            </p>
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
              alt="CRM implementation team"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div>
              {STEPS.map((s, i) => <Step key={i} s={s} i={i} last={i === STEPS.length - 1} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About Customer Maximiser
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Honest answers to the questions we hear most from businesses evaluating Customer Maximiser CRM.
            </p>
            <img
              src="https://images.unsplash.com/photo-1560472355-536de3962603?w=700&q=80"
              alt="CRM FAQ"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Implement CRM?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              See Customer Maximiser in Action
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              Book a free personalised demo — we&apos;ll show you Customer Maximiser configured around your specific sales process and industry.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              No generic walkthrough. A live demo built around your business, your pipeline and your team&apos;s workflow.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                GET FREE DEMO
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                VIEW PRICING
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}